import { useRef, useState, useSyncExternalStore } from 'react';
import { X } from '../../icons';
import { createTaskGroup, type TaskGroupDto } from '../../api/task-extras';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { useEscapeClose } from '../../hooks/useEscapeClose';
import { tProject, getProjectLang, subscribeProjectLang } from '../../lib/project-i18n';
import './MyTasksBoard.css';

interface TaskGroupCreateModalProps {
  onClose: () => void;
  onCreated: (group: TaskGroupDto) => void;
}

/**
 * 그룹 구분용 팔레트 (AW-004-02) — **상태·위험도 색과 역할이 겹치지 않게** 따로 관리한다.
 * 상태색(완료=초록 / 진행중=파랑 / 지연·마감임박=주황 / 위험=빨강)과 같은 뜻으로 읽히면
 * 그룹 색만 보고 "이 그룹이 위험한가?" 로 오해하게 된다. 그래서 여기는 채도를 낮춘
 * 카테고리 색 12종이고, 경고를 뜻하는 순빨강은 넣지 않는다.
 */
export const GROUP_COLORS = [
  '#6366f1', '#3b82f6', '#0ea5e9', '#14b8a6', '#8b5cf6', '#a855f7',
  '#ec4899', '#f472b6', '#f59e0b', '#f97316', '#64748b', '#10b981',
];

/** AW-004-02 — "＋ 그룹 추가" 가 여는 등록 모달. 이름 + 색상만 받는다. */
export function TaskGroupCreateModal({ onClose, onCreated }: TaskGroupCreateModalProps) {
  useSyncExternalStore(subscribeProjectLang, getProjectLang, getProjectLang);
  const trapRef = useFocusTrap<HTMLDivElement>(true);
  const [name, setName] = useState('');
  const [color, setColor] = useState(GROUP_COLORS[3]!);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement | null>(null);

  useEscapeClose(onClose, submitting);

  async function submit() {
    const n = name.trim();
    // 비활성 버튼 대신 이유를 말한다(TeamCreateInviteModal 과 같은 규칙).
    if (!n) {
      setError(tProject('project.group.err_name'));
      nameRef.current?.focus();
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      onCreated(await createTaskGroup({ name: n, color }));
    } catch (err) {
      const code = (err as any)?.response?.data?.code ?? '';
      const msg = (err as any)?.response?.data?.message ?? (err as Error).message;
      setError(`${code ? `[${code}] ` : ''}${msg ?? tProject('project.group.err_save')}`);
      setSubmitting(false);
    }
  }

  return (
    <div
      className="pj-modal-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget && !submitting) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-label={tProject('project.group.title')}
    >
      <div ref={trapRef} className="pj-modal mtg-modal">
        <header className="mtg-head">
          <h2>{tProject('project.group.title')}</h2>
          <button type="button" className="mtg-close" onClick={onClose} disabled={submitting}>
            <X size={14} aria-hidden />
            {tProject('project.btn.close')}
          </button>
        </header>

        <label className="mtg-label" htmlFor="mtg-name">{tProject('project.group.name_label')}</label>
        <input
          id="mtg-name"
          ref={nameRef}
          className="mtg-input"
          aria-invalid={error != null && !name.trim()}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={tProject('project.group.name_ph')}
          maxLength={60}
          autoFocus
          disabled={submitting}
          onKeyDown={(e) => { if (e.key === 'Enter') void submit(); }}
        />

        <div className="mtg-label mtg-label--mt">{tProject('project.group.color_label')}</div>
        <p className="mtg-colorhint">{tProject('project.group.color_hint')}</p>
        <div className="mtg-colors" role="radiogroup" aria-label={tProject('project.group.color_label')}>
          {GROUP_COLORS.map((c) => (
            <button
              key={c}
              type="button"
              role="radio"
              aria-checked={c === color}
              aria-label={c}
              className={`mtg-color${c === color ? ' mtg-color--on' : ''}`}
              style={{ background: c }}
              onClick={() => setColor(c)}
            />
          ))}
        </div>

        {error && <div className="pj-modal-error" role="alert">{error}</div>}

        <footer className="mtg-foot">
          <button type="button" className="mtg-submit" onClick={() => void submit()} disabled={submitting}>
            {submitting ? tProject('project.btn.saving') : tProject('project.group.submit')}
          </button>
          <button type="button" className="mtg-cancel" onClick={onClose} disabled={submitting}>
            {tProject('project.btn.cancel')}
          </button>
        </footer>
      </div>
    </div>
  );
}
